import React, {FC, useEffect, useMemo, useRef, useState} from 'react';

import {cn} from '@bem-react/classname';
import {Plus, TrashBin} from '@gravity-ui/icons';
import {Button, Card} from '@gravity-ui/uikit';
import {SeriesValue} from 'common/models/survey';
import i18n from 'i18n';
import {useFieldArray} from 'react-hook-form';
import focusByNames from 'ui/utils/focusByNames';
import {getDefaultValue} from 'ui/utils/getDefaultValues';
import getSeriesQuestionSlug from 'ui/utils/getSeriesQuestionSlug';

import AriaLive from '../AriaLive/AriaLive';
import MarkdownText from '../MarkdownText';
import Question from '../Question';

import {SeriesProps} from './Series.types';

import './Series.scss';

const cnSeries = cn('Series');

const Series: FC<SeriesProps> = ({series, matchConditions}) => {
    const [status, setStatus] = useState('');
    const [focusField, setFocusField] = useState('');
    const {fields, remove, append} = useFieldArray({
        name: series.id,
    });
    const ref = useRef<Array<HTMLDivElement | null>>([]);
    const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    useEffect(() => {
        return () => {
            const timers = timersRef.current;
            timersRef.current = [];
            return timers.forEach((timer) => timer && clearTimeout(timer));
        };
    }, []);

    useEffect(() => {
        if (focusField) {
            focusByNames([focusField]);
            setFocusField('');
        }
    }, [focusField]);

    const defaultValue = useMemo(() => {
        const result: SeriesValue = {};
        series.items.forEach((question) => {
            result[question.id] = getDefaultValue(question);
        });
        return result;
    }, [series]);

    const onAppend = () => {
        append(defaultValue);
        setStatus(i18n('survey-page', 'sr-status-add-series'));
        setFocusField(getSeriesQuestionSlug(series.id, fields.length, series.items[0]?.id));
    };

    const onDelete = (index: number) => () => {
        ref.current?.[index]?.classList.add('zoom');
        const timer = setTimeout(() => {
            remove(index);
            setStatus(i18n('survey-page', 'sr-status-delete-series'));
        }, 700);
        timersRef.current.push(timer);
        setFocusField(
            getSeriesQuestionSlug(series.id, index === 0 ? 1 : index - 1, series.items[0]?.id),
        );
    };

    return (
        <div className={cnSeries()}>
            <AriaLive status={status} />
            {fields.map((item, index) => (
                <Card
                    key={item.id}
                    className={cnSeries('Card')}
                    ref={(elem) => {
                        ref.current[index] = elem;
                    }}
                >
                    <div className={cnSeries('Header')}>
                        <MarkdownText
                            variant="body-2"
                            as="h2"
                            className={cnSeries('Title')}
                            text={`${series.label ?? i18n('survey-page', 'series-name')} ${
                                index + 1
                            }`}
                        />
                        {fields.length > 1 && (
                            <Button
                                onClick={onDelete(index)}
                                view="flat-danger"
                                title={i18n('survey-page', 'delete-series-btn')}
                            >
                                <Button.Icon>
                                    <TrashBin aria-hidden="true" focusable="false" />
                                </Button.Icon>
                            </Button>
                        )}
                    </div>
                    <div className={cnSeries('Content')}>
                        {series.items.map((question) => {
                            if (
                                question.hidden ||
                                (matchConditions &&
                                    !matchConditions({
                                        conditions: question.conditions,
                                        seriesName: series.id,
                                        index,
                                        question: question.id,
                                    }))
                            ) {
                                return null;
                            }
                            return (
                                <Question
                                    key={question.id}
                                    name={getSeriesQuestionSlug(series.id, index, question.id)}
                                    question={question}
                                />
                            );
                        })}
                    </div>
                </Card>
            ))}
            <Button view="outlined" size="l" onClick={onAppend} className={cnSeries('AddButton')}>
                <Button.Icon>
                    <Plus aria-hidden="true" focusable="false" />
                </Button.Icon>
                {i18n('survey-page', 'add-series')}
            </Button>
        </div>
    );
};

export default Series;
